/**
 * AuthContext — public entry. Picks the mock (Phase 1) or the
 * real-backend (Phase 3) provider based on `REACT_APP_USE_MOCK_DATA`.
 *
 * The two providers write to the SAME React context, so every consumer
 * (`useAuth`) is provider-agnostic. Cut-over is a single env flag.
 *
 * Storage policy:
 *  - Mock path: persona id + session-start in `sessionStorage` (no PII).
 *  - Real path: HttpOnly cookie set by `/api/auth/login`. No tokens
 *    ever touch JS-readable storage.
 */
import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { getPersonasForTenant } from "@/lib/mockData";
import { USE_MOCK_DATA } from "@/lib/api";
import { useTenantData } from "@/contexts/TenantModeContext";
import { logger } from "@/lib/logger";
import { ApiAuthProvider } from "@/contexts/ApiAuthContext";

export const AuthContext = createContext(null);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}

// ─── Mock provider (Phase 1 — still default for local dev when flag on) ──
const STORAGE_KEY = "algos.auth.v1";
const sessionStore = typeof window !== "undefined" ? window.sessionStorage : null;

const readStoredPersonaId = () => {
  if (!sessionStore) return null;
  try {
    const raw = sessionStore.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.userId
      ? { userId: parsed.userId, sessionStartedAt: parsed.sessionStartedAt || Date.now() }
      : null;
  } catch (err) {
    logger.warn("[auth] Failed to read stored session", err);
    return null;
  }
};

const writeSession = (userId, sessionStartedAt) => {
  if (!sessionStore) return;
  try {
    sessionStore.setItem(STORAGE_KEY, JSON.stringify({ userId, sessionStartedAt }));
  } catch (err) {
    logger.warn("[auth] Failed to persist session", err);
  }
};

const clearSession = () => {
  if (!sessionStore) return;
  try { sessionStore.removeItem(STORAGE_KEY); }
  catch (err) { logger.warn("[auth] Failed to clear session", err); }
};

function MockAuthProvider({ children }) {
  const { tenant: activeTenant } = useTenantData();
  const initial = readStoredPersonaId();
  const [personaId, setPersonaId] = useState(initial?.userId || null);
  const [sessionStartedAt, setSessionStartedAt] = useState(initial?.sessionStartedAt || null);

  const user = useMemo(() => {
    if (!personaId) return null;
    const list = getPersonasForTenant(activeTenant?.id);
    return list.find((p) => p.id === personaId) || list[0];
  }, [personaId, activeTenant?.id]);

  const signIn = useCallback((id) => {
    const now = Date.now();
    setPersonaId(id);
    setSessionStartedAt(now);
    writeSession(id, now);
  }, []);

  const signOut = useCallback(() => {
    setPersonaId(null);
    setSessionStartedAt(null);
    clearSession();
  }, []);

  const switchRole = useCallback((id) => {
    const now = Date.now();
    setPersonaId(id);
    setSessionStartedAt(now);
    writeSession(id, now);
    logger.info(`[audit] role.switched actor=${id} ts=${new Date(now).toISOString()}`);
  }, []);

  const extendSession = useCallback(() => {
    const now = Date.now();
    setSessionStartedAt(now);
    if (personaId) writeSession(personaId, now);
  }, [personaId]);

  const value = useMemo(
    () => ({
      user,
      tenant: activeTenant,
      sessionStartedAt,
      hydrated: true,
      isAuthenticated: !!user,
      authMode: "mock",
      error: null,
      signIn,
      signOut,
      switchRole,
      extendSession,
    }),
    [user, activeTenant, sessionStartedAt, signIn, signOut, switchRole, extendSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ─── Selector ─────────────────────────────────────────────────────────
export function AuthProvider({ children }) {
  if (USE_MOCK_DATA) return <MockAuthProvider>{children}</MockAuthProvider>;
  return <ApiAuthProvider>{children}</ApiAuthProvider>;
}
